import {Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
    Countdown,
    DefaultSignatoriesProvider,
    OtpCodeCountdownService,
    PesDocument,
    PesTask,
    PesTaskEventType,
    PesTaskService,
    SignatoriesProvider,
    Signatory,
    SignatureData,
    SignEvent,
    SignMode,
    Step,
    SubscriptionManager,
    TimeUtils,
    Transaction
} from '@pes/ui';
import {ISubscription} from 'rxjs/Subscription';
import {DomSanitizer} from '@angular/platform-browser';
import {finalize} from 'rxjs/operators/finalize';
import {TagCommanderService} from '../../../utils/service/tag-commander.service';
import {UserActionTracerService} from '../../../utils/service/user-action-tracer.service';
import {Observable} from 'rxjs/Observable';
import {TranslateService} from '@ngx-translate/core';
import {concatMap} from 'rxjs/operators/concatMap';

@Component({
    selector: 'lbpa-sign',
    templateUrl: './sign.component.html',
    styleUrls: ['./sign.component.scss']
})
export class SignComponent implements OnInit, OnDestroy {
    @Input() public task: PesTask = null;
    @Input() public stepId: string = null;

    @Output()
    public loader: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output()
    public notifyError: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    public submitTask: EventEmitter<any> = new EventEmitter<any>();

    public transaction: Transaction = null;

    public signatory: Signatory = null;

    public userName: string = null;

    public initiales: string = null;

    public mobilePhone: string = null;

    public showRecap: boolean = true;

    public showSign: boolean = false

    public disabledSign: boolean = true;

    public agreementsChecked: any = [];
    public agreementTexts: any = [];

    public codeOtp: string = null;
    /**
     * Indicates whether the OTP code has been generated and sent (true), or not (false).
     */
    public otpCodeSent: boolean = false;
    /**
     * The list of documents to validate.
     */
    public documents: PesDocument[] = [];
    /**
     * Indicates whether an signature request is performing (true), or not (false).
     */
    public sendingSignature: boolean = false;
    /**
     * Indicates whether the OTP code exists (true), or not (false).
     */
    public otpCodeExists: boolean = false;

    /**
     * The string representation of the OTP time left.
     */
    public otpTimeLeft: string = null;
    /**
     * The reference to the PES task service.
     */
    protected taskService: PesTaskService = null;

    private readonly signatoriesProvider: SignatoriesProvider;

    /**
     * The reference to the OTP code countdown service.
     */
    private readonly _countdownService: OtpCodeCountdownService = null;

    /**
     * The reference to the OTP countdown.
     */
    private _countdown: Countdown = null;

    private readonly tagCommanderService: TagCommanderService = null;

    private readonly userActionTracerService: UserActionTracerService;

    /**
     * The reference to the PES translation service.
     */
    private readonly _translate: TranslateService = null;

    constructor(protected injector: Injector, protected _sanitizer: DomSanitizer) {
        this.taskService = injector.get(PesTaskService);
        this.signatoriesProvider = injector.get(DefaultSignatoriesProvider);
        this._countdownService = injector.get(OtpCodeCountdownService);
        this.tagCommanderService = this.injector.get(TagCommanderService);
        this.userActionTracerService = injector.get(UserActionTracerService);
        this._translate = injector.get(TranslateService);
        SubscriptionManager.getInstance().createSubscribable(this);
    }

    public ngOnInit(): void {
        this.refresh();
    }

    public ngOnDestroy(): void {
        this.freeOtpCodeCountdown();
        SubscriptionManager.getInstance().deleteSubscribable(this);
    }

    public sendOtp(): void {
        if (!this.disabledSign) {
            this.loader.emit(true);
            this.freeOtpCodeCountdown();
            this.traceCgAction().pipe(
                finalize(() => {
                    this.otpCodeSent = true;
                    this.createOtpCodeCountdown();
                }),
                concatMap(() => {
                    return this.taskService.sendTaskEvent(this.task.reference,
                        SignEvent.create(PesTaskEventType.SEND_OTP,
                            null,
                            null,
                            this.signatory.position))
                })
            ).subscribe(() => {
                this.showSign = true;
                this.showRecap = false;
                this.loader.emit(false);
            }, error => {
                this.notifyError.emit(error);
            });
        }
    }

    public checkboxChange(checked: boolean): void {
        this.disabledSign = this.agreementsChecked.filter(c => !c.checked).length > 0;
    }

    public sign(): void {
        if (this.codeOtp && this.codeOtp.length === 6 && this.otpCodeSent) {
            try {
                this.tagCommanderService.click();
            } catch (e) {
                console.warn(e);
            }
            const data: SignatureData = {otpCode: this.codeOtp};
            this.loader.emit(true);
            this.sendingSignature = true;
            this.taskService
                .sendTaskEvent(
                    this.task.reference, SignEvent.create(PesTaskEventType.SIGN, data, null, this.signatory.position)
                )
                .subscribe(() => {
                    this.submitTask.emit(this.task.data);
                }, error => {
                    this.sendingSignature = false;
                    this.loader.emit(false)
                    this.notifyError.emit(error);
                });
        }
    }

    protected refresh(): void {
        const taskRef: string = this.task.reference;

        const sub2: ISubscription = this.taskService.get(taskRef).subscribe((task: PesTask) => {
            this.task = task;
            this.transaction = this.task.data.taskData.transactions[0];
            this.agreementTexts = [this._translate.instant('lbpa.lu_accept'), ...this.transaction.agreementTexts];
            this.agreementTexts.forEach(() => this.agreementsChecked.push({'checked': false}));

            this.buildSignatoryInfo();
            this.buildDocumentList();
            this.loader.emit(false);
        });

        SubscriptionManager.getInstance().registerSubscription(this, sub2);
    }


    /**
     * Creates the OTP countdown.
     */
    protected createOtpCodeCountdown(): void {
        this._countdown = this._countdownService.createCountdown(
            900,
            time => {
                this.otpCodeExists = true;
                this.otpTimeLeft = TimeUtils.timeToString(time);
            },
            () => {
                this.otpCodeExists = false;
                this.otpCodeSent = false;
            }
        );
    }

    /**
     * Finalizes the OTP countdown.
     */
    protected freeOtpCodeCountdown(): void {
        if (this._countdown) {
            this._countdown.stop();
            this._countdown = null;
        }
    }

    private buildSignatoryInfo(): void {
        this.signatory = this.signatoriesProvider.getSignatories(SignMode.DISTANT, this.task)[0];
        this.userName = this.signatory.user.firstName + ' ' + this.signatory.user.lastName;
        this.initiales = this.signatory.user.firstName.charAt(0) + this.signatory.user.lastName.charAt(0);
        this.mobilePhone = this.signatory.user.phone;
    }

    private buildDocumentList() {
        const step = this.task.data.steps.find((s: Step) => {
            return s.id === this.stepId;
        });
        const technicalContext: any = step.technicalContext;
        this.documents = technicalContext.documents.filter(doc => doc.toSign);
    }

    private traceCgAction(): Observable<any> {
        const userId = this.signatoriesProvider.getSignatories(SignMode.DISTANT, this.task)[0].user.id;

        return this.userActionTracerService.traceReadCgAction(
            this.task.reference,
            this.transaction.contraliaId,
            userId,
            this.agreementTexts.map(t => t.replace(/&lt;[^>]*>/g, ''))
        )
    }
}



// WEBPACK FOOTER //
// ./src/app/component/step-parcours-lbpa/sign/sign.component.ts